import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import { INBOX_TYPES } from 'shared/mixins/inboxMixin';
import InboxChannel from '../../api/channel/inboxChannel';
import WhatsappChannel from '../../api/channel/whatsappChannel';
import InboxesAPI from '../../api/inboxes';
import InboxLabelsAPI from '../../api/inboxLabels';
import FoldersAPI from '../../api/folders';
import AnalyticsHelper from '../../helper/AnalyticsHelper';
import { ACCOUNT_EVENTS } from '../../helper/AnalyticsHelper/events';
import { logError } from '../../helper/logger';
import {
  isInboxDeletionCompleted,
  normalizeInboxDeletionRun,
} from '../../helper/inboxDeletion';
import * as types from '../mutation-types';
import { throwErrorMessage } from '../utils/api';

const buildInboxData = (inboxParams) => {
  const formData = new FormData();
  const { channel = {}, ...inboxProperties } = inboxParams;
  Object.keys(inboxProperties).forEach((key) => {
    formData.append(key, inboxProperties[key]);
  });
  const { selectedFeatureFlags, ...channelParams } = channel;
  if (selectedFeatureFlags) {
    if (selectedFeatureFlags.length) {
      selectedFeatureFlags.forEach((featureFlag) => {
        formData.append(`channel[selected_feature_flags][]`, featureFlag);
      });
    } else {
      formData.append('channel[selected_feature_flags][]', '');
    }
  }
  Object.keys(channelParams).forEach((key) => {
    formData.append(`channel[${key}]`, channel[key]);
  });
  return formData;
};

export const state = {
  records: [],
  inboxLabels: {},
  // Cartelle nascoste dalla sidebar, per inbox. Servono solo alla modale di
  // ripristino: l'albero non le riceve proprio, il server le pota prima.
  hiddenInboxLabels: {},
  uiFlags: {
    isFetching: false,
    isFetchingItem: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isUpdatingIMAP: false,
    isUpdatingSMTP: false,
    isFetchingLabels: false,
    // Folder management flags
    isCreatingFolder: false,
    isUpdatingFolder: false,
    isDeletingFolder: false,
    isSyncingFolders: false,
    isRequestingConversationExportOtp: false,
    isCreatingConversationExport: false,
  },
};

export const getters = {
  getInboxes($state) {
    return $state.records;
  },
  getInboxLabels: ($state) => (inboxId) => {
    return $state.inboxLabels[inboxId] || [];
  },
  getHiddenInboxLabels: ($state) => (inboxId) => {
    return $state.hiddenInboxLabels[inboxId] || [];
  },
  getWhatsAppTemplates: ($state) => (inboxId) => {
    const [inbox] = $state.records.filter(
      (record) => record.id === Number(inboxId)
    );

    const {
      message_templates: whatsAppMessageTemplates,
      additional_attributes: additionalAttributes,
    } = inbox || {};

    const { message_templates: apiInboxMessageTemplates } =
      additionalAttributes || {};
    const messagesTemplates =
      whatsAppMessageTemplates || apiInboxMessageTemplates;

    // filtering out the whatsapp templates with media
    if (messagesTemplates instanceof Array) {
      return messagesTemplates.filter((template) => {
        return !template.components.some(
          (i) => i.format === 'IMAGE' || i.format === 'VIDEO'
        );
      });
    }
    return [];
  },
  getNewConversationInboxes($state) {
    return $state.records.filter((inbox) => {
      const { channel_type: channelType } = inbox;

      const isEmailChannel = channelType === INBOX_TYPES.EMAIL;
      return isEmailChannel;
    });
  },
  getInbox: ($state) => (inboxId) => {
    const [inbox] = $state.records.filter(
      (record) => record.id === Number(inboxId)
    );
    return inbox || {};
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getWebsiteInboxes() {
    return [];
  },
};

const sendAnalyticsEvent = (channelType) => {
  AnalyticsHelper.track(ACCOUNT_EVENTS.ADDED_AN_INBOX, {
    channelType,
  });
};

const captureInboxRequestAccountId = () => InboxesAPI.accountIdFromRoute;

const captureInboxCacheContext = () =>
  typeof InboxesAPI.getCacheContext === 'function'
    ? InboxesAPI.getCacheContext()
    : { accountId: captureInboxRequestAccountId() };

const isInboxRequestAccountCurrent = (accountId) =>
  String(InboxesAPI.accountIdFromRoute) === String(accountId);

export const actions = {
  revalidate: async ({ commit, dispatch, state: $state }, { newKey }) => {
    const cacheContext = captureInboxCacheContext();
    const requestAccountId = cacheContext.accountId;
    const hydrateFromNetwork = async () => {
      const response =
        typeof InboxesAPI.getFromNetworkForContext === 'function'
          ? await InboxesAPI.getFromNetworkForContext(cacheContext)
          : await InboxesAPI.getFromNetwork();
      if (!isInboxRequestAccountCurrent(requestAccountId)) return null;

      const payload = response.data.payload;
      if (Array.isArray(payload)) {
        commit(types.default.SET_INBOXES, payload);
      }
      return response;
    };
    const isTabStoreEmpty = !$state.records?.length;

    // An empty per-tab store is an urgent UI recovery path. Bypass the shared
    // cache so a stale key or a blocked IndexedDB connection cannot keep the
    // sidebar blank.
    if (isTabStoreEmpty) {
      try {
        await hydrateFromNetwork();
      } catch (error) {
        logError('[Inboxes] Failed to recover empty tab store', error);
      }
      return;
    }

    try {
      const isExistingKeyValid = await InboxesAPI.validateCacheKeyWithTimeout(
        newKey,
        cacheContext.dataManager
      );
      if (!isExistingKeyValid) {
        if (!isInboxRequestAccountCurrent(requestAccountId)) return;

        const response = await InboxesAPI.refetchAndCommit(
          newKey,
          cacheContext
        );
        if (!isInboxRequestAccountCurrent(requestAccountId)) return;

        const payload = response.data.payload;
        if (Array.isArray(payload)) {
          commit(types.default.SET_INBOXES, payload);
        }
        // La cache IndexedDB non include has_unread (attr_accessor transiente),
        // quindi dopo ogni SET_INBOXES patchamo has_unread dal network
        dispatch('refreshUnreadStatus');
      }
    } catch (error) {
      // A cache timeout must not stall reconnect completion. Recover directly
      // from the network and leave the existing store untouched on failure.
      logError('[Inboxes] Cache revalidation failed; using network', error);
      try {
        await hydrateFromNetwork();
      } catch (networkError) {
        logError('[Inboxes] Failed to revalidate inbox store', networkError);
      }
    }
  },
  get: async ({ commit, dispatch }) => {
    const requestAccountId = captureInboxRequestAccountId();
    commit(types.default.SET_INBOXES_UI_FLAG, { isFetching: true });
    try {
      const response = await InboxesAPI.get(true);
      if (isInboxRequestAccountCurrent(requestAccountId)) {
        commit(types.default.SET_INBOXES, response.data.payload);
      }
    } catch (error) {
      logError('[Inboxes] Failed to hydrate inbox store', error);
    } finally {
      commit(types.default.SET_INBOXES_UI_FLAG, { isFetching: false });
      // This is also the recovery path when cache hydration fails or is
      // blocked by another browser tab.
      if (isInboxRequestAccountCurrent(requestAccountId)) {
        dispatch('refreshUnreadStatus');
      }
    }
  },
  refreshFromNetwork: async ({ commit, dispatch }) => {
    const requestAccountId = captureInboxRequestAccountId();
    commit(types.default.SET_INBOXES_UI_FLAG, { isFetching: true });
    try {
      const response = await InboxesAPI.refreshFromNetwork();
      commit(types.default.SET_INBOXES_UI_FLAG, { isFetching: false });
      if (!isInboxRequestAccountCurrent(requestAccountId)) return [];

      commit(types.default.SET_INBOXES, response.data.payload);
      dispatch('refreshUnreadStatus');
      return response.data.payload;
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isFetching: false });
      throw error;
    }
  },
  // Refresh has_unread dal network (bypassa cache IndexedDB)
  refreshUnreadStatus: async ({ commit, state: $state }) => {
    const requestAccountId = captureInboxRequestAccountId();
    try {
      const response = await InboxesAPI.getFromNetwork();
      if (!isInboxRequestAccountCurrent(requestAccountId)) return [];

      const freshInboxes = response.data.payload;

      if (!Array.isArray(freshInboxes)) return [];

      // The network refresh is authoritative enough to hydrate a new tab. The
      // old implementation only patched existing records, so a failed cache
      // bootstrap left the sidebar empty forever.
      if (!$state.records?.length) {
        commit(types.default.SET_INBOXES, freshInboxes);
        return freshInboxes;
      }

      // Aggiorna solo has_unread sugli inbox esistenti nello store
      freshInboxes.forEach((freshInbox) => {
        const existing = $state.records.find((r) => r.id === freshInbox.id);
        if (existing && existing.has_unread !== freshInbox.has_unread) {
          commit('SET_INBOX_UNREAD_STATUS', {
            inboxId: freshInbox.id,
            hasUnread: !!freshInbox.has_unread,
          });
        }
      });
      return freshInboxes;
    } catch (_) {
      // ignore
      return [];
    }
  },
  createChannel: async ({ commit }, params) => {
    try {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: true });
      const response = await InboxChannel.create(params);
      commit(types.default.ADD_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      const { channel = {} } = params;
      sendAnalyticsEvent(channel.type);
      return response.data;
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      const err = new Error(errorMessage);
      err.errorCode = error?.response?.data?.error_code;
      throw err;
    }
  },
  createWhatsAppEmbeddedSignup: async ({ commit }, params) => {
    try {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: true });
      const response = await WhatsappChannel.createEmbeddedSignup(params);
      commit(types.default.ADD_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      sendAnalyticsEvent('whatsapp');
      return response.data;
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      throw error;
    }
  },
  updateInbox: async ({ commit }, { id, formData = true, ...inboxParams }) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isUpdating: true });
    try {
      const response = await InboxesAPI.update(
        id,
        formData ? buildInboxData(inboxParams) : inboxParams
      );
      commit(types.default.EDIT_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdating: false });
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdating: false });
      throwErrorMessage(error);
    }
  },
  updateInboxIMAP: async ({ commit }, { id, ...inboxParams }) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingIMAP: true });
    try {
      const response = await InboxesAPI.update(id, inboxParams);
      commit(types.default.EDIT_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingIMAP: false });
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingIMAP: false });
      throwErrorMessage(error);
    }
  },
  updateInboxSMTP: async ({ commit }, { id, ...inboxParams }) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingSMTP: true });
    try {
      const response = await InboxesAPI.update(id, inboxParams);
      commit(types.default.EDIT_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingSMTP: false });
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingSMTP: false });
      throwErrorMessage(error);
    }
  },
  delete: async ({ commit }, inboxId) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isDeleting: true });
    try {
      const response = await InboxesAPI.delete(inboxId);
      const deletionRun = normalizeInboxDeletionRun(response.data, inboxId);
      if (!deletionRun.id) {
        const protocolError = new Error(
          'Inbox deletion response is missing deletion_run_id'
        );
        protocolError.code = 'INBOX_DELETION_RUN_MISSING';
        throw protocolError;
      }
      if (isInboxDeletionCompleted(deletionRun.status)) {
        commit(types.default.DELETE_INBOXES, inboxId);
      }
      commit(types.default.SET_INBOXES_UI_FLAG, { isDeleting: false });
      return deletionRun;
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isDeleting: false });
      throw error;
    }
  },
  getInboxDeletion: async ({ commit }, { inboxId }) => {
    const response = await InboxesAPI.getDeletionStatus(inboxId);
    const deletionRun = normalizeInboxDeletionRun(response.data, inboxId);
    if (isInboxDeletionCompleted(deletionRun.status)) {
      commit(types.default.DELETE_INBOXES, inboxId);
    }
    return deletionRun;
  },
  reauthorizeEmail: async ({ commit }, { inboxId }) => {
    try {
      const response = await InboxesAPI.reauthorize(inboxId);
      const inboxesResponse = await InboxesAPI.get(true);
      commit(types.default.SET_INBOXES, inboxesResponse.data.payload);
      return response.data;
    } catch (error) {
      throw new Error(error?.response?.data?.message);
    }
  },
  deleteInboxAvatar: async (_, inboxId) => {
    try {
      await InboxesAPI.deleteInboxAvatar(inboxId);
    } catch (error) {
      throw new Error(error);
    }
  },
  fetchInboxLabels: async ({ commit }, inboxId) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isFetchingLabels: true });
    try {
      const response = await InboxLabelsAPI.getLabels(inboxId);
      commit('SET_INBOX_LABELS', { inboxId, labels: response.data });
      commit(types.default.SET_INBOXES_UI_FLAG, { isFetchingLabels: false });
      return response.data;
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isFetchingLabels: false });
      throw new Error(error);
    }
  },
  fetchHiddenInboxLabels: async ({ commit }, inboxId) => {
    const response = await InboxLabelsAPI.getHiddenLabels(inboxId);
    const labels = response.data?.payload || [];
    commit('SET_HIDDEN_INBOX_LABELS', { inboxId, labels });
    return labels;
  },

  /**
   * Nasconde o rimostra una cartella nella sidebar dell'account.
   * Nessun aggiornamento ottimistico: il server pota già i sottoalberi nascosti,
   * quindi si ricarica l'albero e non serve replicare quella logica qui.
   */
  setInboxLabelHidden: async (
    { commit, dispatch },
    { inboxId, labelId, hidden }
  ) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingFolder: true });
    try {
      const response = await InboxLabelsAPI.setLabelHidden(
        inboxId,
        labelId,
        hidden
      );
      commit('SET_HIDDEN_INBOX_LABELS', {
        inboxId,
        labels: response.data?.payload || [],
      });
      await dispatch('fetchInboxLabels', inboxId);
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingFolder: false });
      return response.data;
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingFolder: false });
      const errorMessage = error?.response?.data?.error || error.message;
      throw new Error(errorMessage);
    }
  },

  refreshGmailCounts: async ({ commit }, inboxId) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isFetchingLabels: true });
    try {
      const response = await InboxesAPI.refreshGmailCounts(inboxId);
      commit(types.default.SET_INBOXES_UI_FLAG, { isFetchingLabels: false });
      return response.data;
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isFetchingLabels: false });
      throw new Error(error);
    }
  },
  requestConversationExportOtp: async ({ commit }, inboxId) => {
    commit(types.default.SET_INBOXES_UI_FLAG, {
      isRequestingConversationExportOtp: true,
    });
    try {
      const response = await InboxesAPI.requestConversationExportOtp(inboxId);
      commit(types.default.SET_INBOXES_UI_FLAG, {
        isRequestingConversationExportOtp: false,
      });
      return response.data;
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, {
        isRequestingConversationExportOtp: false,
      });
      throw new Error(error?.response?.data?.error || error.message);
    }
  },
  createConversationExport: async ({ commit }, { inboxId, params }) => {
    commit(types.default.SET_INBOXES_UI_FLAG, {
      isCreatingConversationExport: true,
    });
    try {
      const response = await InboxesAPI.createConversationExport(
        inboxId,
        params
      );
      commit(types.default.SET_INBOXES_UI_FLAG, {
        isCreatingConversationExport: false,
      });
      return response.data;
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, {
        isCreatingConversationExport: false,
      });
      throw new Error(error?.response?.data?.error || error.message);
    }
  },

  // ========== FOLDER MANAGEMENT ACTIONS ==========

  /**
   * Create a new folder/label in an email inbox
   * @param {number} inboxId - The inbox ID
   * @param {Object} params - Folder parameters
   * @param {string} params.name - Folder name
   * @param {string} [params.parentSourceId] - Parent folder source_id for nested folders
   * @param {string} [params.color] - Folder color (Gmail only)
   */
  createFolder: async (
    { commit, dispatch },
    { inboxId, name, parentSourceId, color }
  ) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isCreatingFolder: true });
    try {
      const response = await FoldersAPI.create(inboxId, {
        name,
        parentSourceId,
        color,
      });
      // Reload labels to reflect the new folder
      await dispatch('fetchInboxLabels', inboxId);
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreatingFolder: false });
      return response.data;
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreatingFolder: false });
      const errorCode = error?.response?.data?.error_code;
      const errorMessage = error?.response?.data?.error || error.message;
      const err = new Error(errorMessage);
      err.errorCode = errorCode;
      throw err;
    }
  },

  /**
   * Update an existing folder/label
   * @param {number} inboxId - The inbox ID
   * @param {string} sourceId - The folder's source_id
   * @param {Object} params - Update parameters
   * @param {string} [params.name] - New folder name
   * @param {string} [params.color] - New folder color (Gmail only)
   */
  updateFolder: async (
    { commit, dispatch },
    { inboxId, sourceId, name, color }
  ) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingFolder: true });
    try {
      const response = await FoldersAPI.update(inboxId, sourceId, {
        name,
        color,
      });
      // Reload labels to reflect the changes
      await dispatch('fetchInboxLabels', inboxId);
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingFolder: false });
      return response.data;
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingFolder: false });
      const errorMessage = error?.response?.data?.error || error.message;
      throw new Error(errorMessage);
    }
  },

  /**
   * Delete a folder/label
   * @param {number} inboxId - The inbox ID
   * @param {string} sourceId - The folder's source_id
   */
  deleteFolder: async ({ commit, dispatch }, { inboxId, sourceId }) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isDeletingFolder: true });
    try {
      const response = await FoldersAPI.delete(inboxId, sourceId);
      // Reload labels to reflect the deletion
      await dispatch('fetchInboxLabels', inboxId);
      commit(types.default.SET_INBOXES_UI_FLAG, { isDeletingFolder: false });
      return response.data;
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isDeletingFolder: false });
      const errorCode = error?.response?.data?.error_code;
      const errorMessage = error?.response?.data?.error || error.message;
      const err = new Error(errorMessage);
      err.errorCode = errorCode;
      throw err;
    }
  },

  /**
   * Force sync folders from email provider
   * @param {number} inboxId - The inbox ID
   */
  syncFolders: async ({ commit, dispatch }, inboxId) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isSyncingFolders: true });
    try {
      const response = await FoldersAPI.sync(inboxId);
      commit('SET_INBOX_REAUTHORIZATION_STATUS', {
        inboxId,
        reauthorizationRequired: false,
      });
      // Reload labels after sync
      await dispatch('fetchInboxLabels', inboxId);
      commit(types.default.SET_INBOXES_UI_FLAG, { isSyncingFolders: false });
      return response.data;
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isSyncingFolders: false });
      const errorMessage = error?.response?.data?.error || error.message;
      throw new Error(errorMessage);
    }
  },
};

export const mutations = {
  [types.default.SET_INBOXES_UI_FLAG]($state, uiFlag) {
    $state.uiFlags = { ...$state.uiFlags, ...uiFlag };
  },
  [types.default.SET_INBOXES]: MutationHelpers.set,
  [types.default.SET_INBOXES_ITEM]: MutationHelpers.setSingleRecord,
  [types.default.ADD_INBOXES]: MutationHelpers.create,
  [types.default.EDIT_INBOXES]: MutationHelpers.update,
  [types.default.DELETE_INBOXES]: MutationHelpers.destroy,
  SET_INBOX_LABELS($state, { inboxId, labels }) {
    $state.inboxLabels = {
      ...$state.inboxLabels,
      [inboxId]: labels,
    };
  },
  SET_HIDDEN_INBOX_LABELS($state, { inboxId, labels }) {
    $state.hiddenInboxLabels = {
      ...$state.hiddenInboxLabels,
      [inboxId]: labels,
    };
  },
  SET_INBOX_UNREAD_STATUS($state, { inboxId, hasUnread }) {
    const inbox = $state.records.find((r) => r.id === Number(inboxId));
    if (inbox) {
      inbox.has_unread = hasUnread;
    }
  },
  SET_INBOX_REAUTHORIZATION_STATUS(
    $state,
    { inboxId, reauthorizationRequired }
  ) {
    const inbox = $state.records.find((r) => r.id === Number(inboxId));
    if (inbox) {
      inbox.reauthorization_required = reauthorizationRequired;
    }
  },
  SET_LABEL_UNREAD_STATUS($state, { inboxId, labelId, hasUnread }) {
    const labels = $state.inboxLabels[inboxId];
    if (!labels) return;

    const setInTree = (labelList) =>
      labelList.some((label) => {
        if (String(label.id) === String(labelId)) {
          label.has_unread = hasUnread;
          return true;
        }
        return Boolean(label.sub_labels?.length && setInTree(label.sub_labels));
      });

    setInTree(labels);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
